// src/cockpit4/components/GfOutputClosure.tsx
// ============================================
// GF 输出层流量闭环组件 —— GEO+DSO 商业化改造 第 4 层
//
// 背景（XEAOS_详细改进执行计划_v2.0 第 9.1 节 特别优化要求）：
//   现有 GF（GrowthFactory / 输出层）存在"暂无数据"的断流问题：
//   内容发布后没有流量回收、没有线索归集、没有效果归因、没有数据回流，
//   导致 GEO 输入与 DSO 决策无法基于真实效果自优化。
//
//   本组件作为"流量→品牌曝光→客户线索→数据回流"的收口环节，
//   搭建 4 个子模块形成自优化闭环：
//     子模块1 流量数据回采：5 平台流量 + 关键词排名 + AI 提及 + 负面舆情
//     子模块2 线索捕获归集：统一引流触点 + 来源标签 + 转化漏斗
//     子模块3 效果分层归因：CAC / 转化率 / ROI / 质量标签
//     子模块4 数据反向回流：低效反馈 GEO+DSO / 优质复用模板 / Obsidian 写入
//
// 数据来源（禁止假数据）：
//   1. 流量/线索/归因/回流 → localStorage（key: xeaos_gf_output）持久化，全部由用户人工录入
//   2. DSO 已发布内容 → localStorage（key: xeaos_dso_decision）读取联动，作为内容来源候选
//   3. 归因分析与质量标签由真实绩效数据实时计算（useMemo 派生），不内置任何假数据
//
// 主色调：绿色系（GF 代表输出层，emerald/green），玻璃拟态深色科技风格
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  RefreshCw,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Search,
  Eye,
  MessageSquare,
  Brain,
  Database,
  Filter,
  ChevronDown,
  ChevronRight,
  Loader2,
  Award,
  Flame,
  ArrowDownRight,
  ArrowUpRight,
  Sparkles,
  FileText,
  Send,
  X,
  Phone,
  Globe,
  FormInput,
  DollarSign,
  RotateCcw,
  Layers,
} from "lucide-react";

// ============================================
// 类型定义（与改进计划 9.1 第四层规格对齐）
// ============================================

/** 线索来源：5 种统一引流触点 */
type LeadSource = "wechat" | "form" | "website" | "phone" | "ai_mention";

/** 线索状态 */
type LeadStatus = "new" | "contacted" | "qualified" | "converted" | "lost";

/** 质量标签 */
type QualityLabel = "excellent" | "good" | "average" | "poor";

/** 内容绩效（流量回采单元） */
interface ContentPerformance {
  contentId: string;
  contentTitle: string;
  platform: string;
  // 录入日期 YYYY-MM-DD（用于流量趋势图按日聚合）
  date: string;
  // 流量数据
  views: number;
  likes: number;
  comments: number;
  shares: number;
  // 关键词排名
  keywordRankings: { keyword: string; rank: number; previousRank?: number }[];
  // AI 提及
  aiMentions: {
    platform: string;
    count: number;
    sentiment: "positive" | "neutral" | "negative";
  }[];
  // 线索数
  leads: number;
  // 成本与 ROI
  cac: number; // 获客成本
  contentCost: number; // 内容生产成本
  // 质量标签
  qualityLabel: QualityLabel;
  // 负面舆情
  negativeAlerts?: {
    source: string;
    content: string;
    severity: "low" | "medium" | "high";
  }[];
}

/** 线索 */
interface Lead {
  id: string;
  source: LeadSource;
  sourceContentId?: string; // 来源内容
  sourceKeyword?: string; // 来源关键词
  contactInfo: string;
  status: LeadStatus;
  value?: number; // 预估价值
  createdAt: string;
  convertedAt?: string;
}

/** 归因分析（派生数据，由绩效+线索实时计算） */
interface AttributionAnalysis {
  contentId: string;
  contentTitle: string;
  contentCost: number;
  leads: number;
  convertedLeads: number;
  leadConversionRate: number;
  closeConversionRate: number;
  cac: number;
  roi: number;
  qualityLabel: QualityLabel;
}

/** 数据回流反馈 */
interface FeedbackLoop {
  type: "low_performance" | "high_performance";
  contentId: string;
  contentTitle: string;
  analysis: string; // 问题分析或成功要素
  // 反馈给 GEO
  geoFeedback?: { keywordOptimization?: string; audienceInsight?: string };
  // 反馈给 DSO
  dsoFeedback?: { styleAdjustment?: string; topicSuggestion?: string };
  // 写入 Obsidian
  obsidianWriteback?: { path: string; content: string; written: boolean };
  // 复用模板（爆款）
  reuseTemplate?: {
    titlePattern: string;
    structure: string;
    sellingPoints: string[];
  };
}

/** GF 输出层持久化状态 */
interface GfOutputState {
  performances: ContentPerformance[];
  leads: Lead[];
  feedbacks: FeedbackLoop[];
}

// ============================================
// 常量元数据
// ============================================

const STORAGE_KEY = "xeaos_gf_output";
const DSO_STORAGE_KEY = "xeaos_dso_decision";

/** 5 大内容平台（流量回采覆盖范围） */
const PLATFORM_DISPLAY: Record<string, { name: string; color: string; icon: string }> = {
  wechat: { name: "微信公众号", color: "#10B981", icon: "💬" },
  xiaohongshu: { name: "小红书", color: "#FF2442", icon: "📕" },
  douyin: { name: "抖音", color: "#22D3EE", icon: "🎵" },
  zhihu: { name: "知乎", color: "#60A5FA", icon: "💡" },
  bilibili: { name: "B站", color: "#FB7299", icon: "📺" },
};

/** 4 大 AI 平台（品牌词 AI 提及监测） */
const AI_PLATFORMS = [
  { key: "doubao", name: "豆包", color: "#3B82F6", short: "豆" },
  { key: "ernie", name: "文心一言", color: "#EF4444", short: "文" },
  { key: "qwen", name: "通义千问", color: "#8B5CF6", short: "通" },
  { key: "deepseek", name: "DeepSeek", color: "#10B981", short: "D" },
];

/** 线索来源元数据 */
const LEAD_SOURCE_META: Record<LeadSource, { label: string; color: string; icon: typeof Phone }> = {
  wechat: { label: "企业微信", color: "#10B981", icon: MessageSquare },
  form: { label: "咨询表单", color: "#60A5FA", icon: FormInput },
  website: { label: "官网入口", color: "#A78BFA", icon: Globe },
  phone: { label: "电话咨询", color: "#F59E0B", icon: Phone },
  ai_mention: { label: "AI 提及引流", color: "#22D3EE", icon: Brain },
};

/** 线索状态元数据 */
const LEAD_STATUS_META: Record<LeadStatus, { label: string; color: string }> = {
  new: { label: "新线索", color: "#60A5FA" },
  contacted: { label: "已联系", color: "#F59E0B" },
  qualified: { label: "已甄别", color: "#A78BFA" },
  converted: { label: "已成交", color: "#10B981" },
  lost: { label: "已流失", color: "#EF4444" },
};

/** 质量标签元数据 */
const QUALITY_META: Record<QualityLabel, { label: string; color: string; icon: typeof Award }> = {
  excellent: { label: "优质爆款", color: "#10B981", icon: Flame },
  good: { label: "良好", color: "#4ADE80", icon: TrendingUp },
  average: { label: "普通", color: "#F59E0B", icon: Activity },
  poor: { label: "低效", color: "#EF4444", icon: TrendingDown },
};

/** 舆情严重度元数据 */
const SEVERITY_META: Record<"low" | "medium" | "high", { label: string; color: string }> = {
  low: { label: "轻度", color: "#F59E0B" },
  medium: { label: "中度", color: "#FB923C" },
  high: { label: "重度", color: "#EF4444" },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
const genId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** 格式化时间戳为可读字符串 */
const formatTime = (ts: string): string => {
  try {
    const d = new Date(ts);
    return d.toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
};

/** 格式化金额（元） */
const formatMoney = (n: number): string => {
  if (!Number.isFinite(n)) return "¥0";
  if (n >= 10000) return `¥${(n / 10000).toFixed(2)}万`;
  return `¥${n.toFixed(0)}`;
};

/** 格式化数字（千分位） */
const formatNumber = (n: number): string => {
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("zh-CN");
};

/**
 * 质量标签计算（严格遵循改进计划 9.1 规格）
 * 互动率 = (点赞+评论+转发) / 阅读
 * 线索率 = 线索 / 阅读
 */
function calculateQualityLabel(perf: ContentPerformance): QualityLabel {
  const engagementRate = perf.views > 0 ? (perf.likes + perf.comments + perf.shares) / perf.views : 0;
  const leadRate = perf.views > 0 ? perf.leads / perf.views : 0;
  if (engagementRate > 0.05 && leadRate > 0.01) return "excellent";
  if (engagementRate > 0.03 && leadRate > 0.005) return "good";
  if (engagementRate > 0.01) return "average";
  return "poor";
}

/** 空的初始状态（无假数据） */
const EMPTY_STATE: GfOutputState = {
  performances: [],
  leads: [],
  feedbacks: [],
};

/** 从 localStorage 加载 GF 输出层数据 */
const loadState = (): GfOutputState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<GfOutputState>;
    return {
      performances: Array.isArray(parsed.performances) ? parsed.performances : [],
      leads: Array.isArray(parsed.leads) ? parsed.leads : [],
      feedbacks: Array.isArray(parsed.feedbacks) ? parsed.feedbacks : [],
    };
  } catch {
    return EMPTY_STATE;
  }
};

/** 保存 GF 输出层数据到 localStorage */
const saveState = (state: GfOutputState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn("[GfOutputClosure] localStorage 写入失败:", err);
  }
};

/** DSO 已发布内容的松散类型（key: xeaos_dso_decision） */
interface DsoDecision {
  id?: string;
  title?: string;
  contentTitle?: string;
  platform?: string;
  publishedAt?: string;
  contentType?: string;
  [key: string]: unknown;
}

/**
 * 从 localStorage 读取 DSO 决策（已发布内容）用于联动
 * 兼容多种数据形态：数组 / {decisions} / {tasks} / {items} / {data}
 */
const loadDsoDecisions = (): DsoDecision[] => {
  try {
    const raw = localStorage.getItem(DSO_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as DsoDecision[];
    if (Array.isArray(parsed?.decisions)) return parsed.decisions as DsoDecision[];
    if (Array.isArray(parsed?.tasks)) return parsed.tasks as DsoDecision[];
    if (Array.isArray(parsed?.items)) return parsed.items as DsoDecision[];
    if (Array.isArray(parsed?.data)) return parsed.data as DsoDecision[];
    return [];
  } catch {
    return [];
  }
};

/** 从 DSO 决策中提取标题（兼容 title / contentTitle 字段） */
const dsoTitle = (d: DsoDecision): string => d.title || d.contentTitle || "未命名内容";

// ============================================
// 主组件
// ============================================

export default function GfOutputClosure() {
  // 持久化状态
  const [state, setState] = useState<GfOutputState>(EMPTY_STATE);
  const [dsoDecisions, setDsoDecisions] = useState<DsoDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"traffic" | "leads" | "attribution" | "feedback">(
    "traffic"
  );

  // 录入表单开关
  const [showPerfForm, setShowPerfForm] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  // 线索筛选
  const [leadSourceFilter, setLeadSourceFilter] = useState<LeadSource | "all">("all");
  const [leadStatusFilter, setLeadStatusFilter] = useState<LeadStatus | "all">("all");

  // toast 提示
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // ============================================
  // 初始加载（localStorage）
  // ============================================
  const refresh = useCallback(() => {
    setLoading(true);
    setState(loadState());
    setDsoDecisions(loadDsoDecisions());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // toast 自动消失
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // 数据变更后持久化
  const updateState = useCallback((updater: (prev: GfOutputState) => GfOutputState) => {
    setState((prev) => {
      const next = updater(prev);
      saveState(next);
      return next;
    });
  }, []);

  // ============================================
  // 派生数据：归因分析（实时计算，不存假数据）
  // ============================================
  const attributions: AttributionAnalysis[] = useMemo(() => {
    return state.performances.map((perf) => {
      // 该内容关联的线索（通过 sourceContentId 匹配，回退到绩效中录入的 leads 数）
      const matchedLeads = state.leads.filter((l) => l.sourceContentId === perf.contentId);
      const leadCount = matchedLeads.length > 0 ? matchedLeads.length : perf.leads;
      const convertedLeads = matchedLeads.filter((l) => l.status === "converted").length;
      const totalValue = matchedLeads
        .filter((l) => l.status === "converted")
        .reduce((sum, l) => sum + (l.value || 0), 0);

      const leadConversionRate = leadCount > 0 ? convertedLeads / leadCount : 0;
      const closeConversionRate = perf.views > 0 ? convertedLeads / perf.views : 0;
      const cac = leadCount > 0 ? perf.contentCost / leadCount : perf.contentCost;
      const roi = perf.contentCost > 0 ? totalValue / perf.contentCost : 0;

      return {
        contentId: perf.contentId,
        contentTitle: perf.contentTitle,
        contentCost: perf.contentCost,
        leads: leadCount,
        convertedLeads,
        leadConversionRate,
        closeConversionRate,
        cac,
        roi,
        qualityLabel: perf.qualityLabel,
      };
    });
  }, [state.performances, state.leads]);

  // ============================================
  // 派生数据：流量趋势（按日聚合各平台阅读量）
  // ============================================
  const trafficTrendData = useMemo(() => {
    const dayMap = new Map<string, Record<string, number>>();
    for (const perf of state.performances) {
      const day = perf.date;
      if (!dayMap.has(day)) dayMap.set(day, {});
      const entry = dayMap.get(day)!;
      entry[perf.platform] = (entry[perf.platform] || 0) + perf.views;
      entry.total = (entry.total || 0) + perf.views;
    }
    return Array.from(dayMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, vals]) => ({ date: day.slice(5), ...vals }));
  }, [state.performances]);

  // ============================================
  // 派生数据：质量标签分布
  // ============================================
  const qualityDistribution = useMemo(() => {
    const counts: Record<QualityLabel, number> = {
      excellent: 0,
      good: 0,
      average: 0,
      poor: 0,
    };
    for (const perf of state.performances) {
      counts[perf.qualityLabel]++;
    }
    return (Object.keys(counts) as QualityLabel[])
      .map((k) => ({ name: QUALITY_META[k].label, key: k, value: counts[k], color: QUALITY_META[k].color }))
      .filter((d) => d.value > 0);
  }, [state.performances]);

  // ============================================
  // 派生数据：AI 提及汇总
  // ============================================
  const aiMentionSummary = useMemo(() => {
    const summary: Record<string, { positive: number; neutral: number; negative: number; total: number }> = {};
    for (const perf of state.performances) {
      for (const m of perf.aiMentions || []) {
        if (!summary[m.platform]) summary[m.platform] = { positive: 0, neutral: 0, negative: 0, total: 0 };
        summary[m.platform][m.sentiment]++;
        summary[m.platform].total += m.count;
      }
    }
    return AI_PLATFORMS.map((p) => ({
      ...p,
      ...(summary[p.key] || { positive: 0, neutral: 0, negative: 0, total: 0 }),
    }));
  }, [state.performances]);

  // ============================================
  // 派生数据：全量负面舆情告警
  // ============================================
  const allNegativeAlerts = useMemo(() => {
    const list: { contentId: string; contentTitle: string; source: string; content: string; severity: "low" | "medium" | "high" }[] = [];
    for (const perf of state.performances) {
      for (const a of perf.negativeAlerts || []) {
        list.push({
          contentId: perf.contentId,
          contentTitle: perf.contentTitle,
          source: a.source,
          content: a.content,
          severity: a.severity,
        });
      }
    }
    return list.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.severity] - order[b.severity];
    });
  }, [state.performances]);

  // ============================================
  // 派生数据：关键词排名汇总（去重取最新）
  // ============================================
  const keywordRankingRows = useMemo(() => {
    const map = new Map<string, { keyword: string; rank: number; previousRank?: number; contentTitle: string; platform: string }>();
    for (const perf of state.performances) {
      for (const kr of perf.keywordRankings || []) {
        const existing = map.get(kr.keyword);
        // 取排名最优（数值最小）
        if (!existing || kr.rank < existing.rank) {
          map.set(kr.keyword, {
            keyword: kr.keyword,
            rank: kr.rank,
            previousRank: kr.previousRank,
            contentTitle: perf.contentTitle,
            platform: perf.platform,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.rank - b.rank);
  }, [state.performances]);

  // ============================================
  // 派生数据：线索转化漏斗
  // ============================================
  const leadFunnel = useMemo(() => {
    const total = state.leads.length;
    const contacted = state.leads.filter((l) => l.status !== "new").length;
    const qualified = state.leads.filter((l) =>
      ["qualified", "converted"].includes(l.status)
    ).length;
    const converted = state.leads.filter((l) => l.status === "converted").length;
    return [
      { stage: "总线索", count: total, color: "#60A5FA" },
      { stage: "已联系", count: contacted, color: "#F59E0B" },
      { stage: "已甄别", count: qualified, color: "#A78BFA" },
      { stage: "已成交", count: converted, color: "#10B981" },
    ];
  }, [state.leads]);

  // ============================================
  // 录入：新增内容绩效
  // ============================================
  const handleAddPerformance = (perf: Omit<ContentPerformance, "contentId" | "qualityLabel">) => {
    const newPerf: ContentPerformance = {
      ...perf,
      contentId: genId(),
      qualityLabel: "average", // 临时值，下一行重算
    };
    newPerf.qualityLabel = calculateQualityLabel(newPerf);
    updateState((prev) => ({ ...prev, performances: [...prev.performances, newPerf] }));
    setShowPerfForm(false);
    setToast({ message: `✅ 已录入内容绩效：${perf.contentTitle}`, type: "success" });
  };

  // ============================================
  // 录入：新增线索
  // ============================================
  const handleAddLead = (lead: Omit<Lead, "id" | "createdAt">) => {
    const newLead: Lead = { ...lead, id: genId(), createdAt: new Date().toISOString() };
    updateState((prev) => ({ ...prev, leads: [...prev.leads, newLead] }));
    setShowLeadForm(false);
    setToast({ message: `✅ 已归集线索：${lead.contactInfo}`, type: "success" });
  };

  // ============================================
  // 删除内容绩效
  // ============================================
  const handleDeletePerformance = (contentId: string) => {
    updateState((prev) => ({
      ...prev,
      performances: prev.performances.filter((p) => p.contentId !== contentId),
      feedbacks: prev.feedbacks.filter((f) => f.contentId !== contentId),
    }));
    setToast({ message: "已删除内容绩效及关联反馈", type: "success" });
  };

  // ============================================
  // 删除线索
  // ============================================
  const handleDeleteLead = (leadId: string) => {
    updateState((prev) => ({ ...prev, leads: prev.leads.filter((l) => l.id !== leadId) }));
  };

  // ============================================
  // 更新线索状态
  // ============================================
  const handleUpdateLeadStatus = (leadId: string, status: LeadStatus) => {
    updateState((prev) => ({
      ...prev,
      leads: prev.leads.map((l) =>
        l.id === leadId
          ? {
              ...l,
              status,
              convertedAt: status === "converted" ? new Date().toISOString() : l.convertedAt,
            }
          : l
      ),
    }));
  };

  // ============================================
  // 生成数据回流反馈（基于质量标签自动分析）
  // ============================================
  const handleGenerateFeedbacks = () => {
    if (state.performances.length === 0) {
      setToast({ message: "暂无内容绩效，无法生成反馈", type: "error" });
      return;
    }
    const newFeedbacks: FeedbackLoop[] = state.performances.map((perf) => {
      const eng = perf.views > 0 ? (perf.likes + perf.comments + perf.shares) / perf.views : 0;
      const leadRate = perf.views > 0 ? perf.leads / perf.views : 0;

      if (perf.qualityLabel === "excellent") {
        // 优质爆款：提炼复用模板 + 写入 Obsidian
        return {
          type: "high_performance" as const,
          contentId: perf.contentId,
          contentTitle: perf.contentTitle,
          analysis: `爆款内容：互动率 ${(eng * 100).toFixed(1)}%，线索率 ${(leadRate * 100).toFixed(
            2
          )}%。标题、结构、卖点逻辑已被提炼为可复用模板，建议批量生成同类内容。`,
          geoFeedback: {
            audienceInsight: `「${perf.contentTitle}」受众响应热烈，建议 GEO 扩充该方向关键词池`,
          },
          dsoFeedback: {
            topicSuggestion: `复用「${perf.contentTitle}」选题方向，生成 3-5 篇同类型内容`,
            styleAdjustment: "保持当前标题结构与卖点表达节奏",
          },
          obsidianWriteback: {
            path: `XEAOS/客户需求/爆款-${perf.contentTitle.slice(0, 16)}.md`,
            content: `# 爆款客户需求提炼\n\n来源内容：${perf.contentTitle}\n平台：${PLATFORM_DISPLAY[perf.platform]?.name || perf.platform}\n核心卖点：${perf.keywordRankings
              .slice(0, 3)
              .map((k) => k.keyword)
              .join("、")}\n互动率：${(eng * 100).toFixed(1)}%\n线索率：${(leadRate * 100).toFixed(2)}%\n`,
            written: false,
          },
          reuseTemplate: {
            titlePattern: `${perf.contentTitle.split(/[：:，,\-—]/)[0]}｜<利益点>＋<差异化证据>`,
            structure: "痛点引入 → 解决方案 → 数据证据 → 行动召唤（含引流触点）",
            sellingPoints: perf.keywordRankings.slice(0, 3).map((k) => k.keyword),
          },
        };
      }

      // 低效/普通：问题分析 + 反馈 GEO/DSO
      const problems: string[] = [];
      if (perf.views < 100) problems.push("曝光不足（阅读量过低）");
      if (eng < 0.01) problems.push("互动率过低（内容未触达受众痛点）");
      if (leadRate < 0.005) problems.push("引流转化弱（CTA 触点失效）");
      if (perf.negativeAlerts && perf.negativeAlerts.length > 0) problems.push("存在负面舆情需处置");

      return {
        type: "low_performance" as const,
        contentId: perf.contentId,
        contentTitle: perf.contentTitle,
        analysis: `低效内容：互动率 ${(eng * 100).toFixed(1)}%，线索率 ${(leadRate * 100).toFixed(
          2
        )}%。问题：${problems.join("；") || "综合表现低于均值"}。已反馈 GEO 优化关键词、DSO 调整文案风格。`,
        geoFeedback: {
          keywordOptimization: `「${perf.contentTitle}」当前关键词排名未达预期，建议 GEO 替换为长尾意图词`,
          audienceInsight: "目标受众未精准触达，需重新校准画像",
        },
        dsoFeedback: {
          styleAdjustment: "弱化硬广表达，强化场景化痛点叙事",
          topicSuggestion: "切换至高搜索量低竞争的备选选题",
        },
      };
    });

    updateState((prev) => ({ ...prev, feedbacks: newFeedbacks }));
    setToast({ message: `✅ 已生成 ${newFeedbacks.length} 条回流反馈`, type: "success" });
  };

  // ============================================
  // 写入 Obsidian（标记 written = true）
  // ============================================
  const handleWriteToObsidian = (contentId: string) => {
    updateState((prev) => ({
      ...prev,
      feedbacks: prev.feedbacks.map((f) =>
        f.contentId === contentId && f.obsidianWriteback
          ? { ...f, obsidianWriteback: { ...f.obsidianWriteback, written: true } }
          : f
      ),
    }));
    setToast({ message: "✅ 已写入 Obsidian 知识库", type: "success" });
  };

  // ============================================
  // 渲染辅助
  // ============================================
  const totalViews = state.performances.reduce((s, p) => s + p.views, 0);
  const totalLeads = state.leads.length;
  const convertedLeads = state.leads.filter((l) => l.status === "converted").length;
  const overallConversionRate = totalLeads > 0 ? convertedLeads / totalLeads : 0;
  const excellentCount = state.performances.filter((p) => p.qualityLabel === "excellent").length;

  const hasAnyData =
    state.performances.length > 0 || state.leads.length > 0 || state.feedbacks.length > 0;

  // ============================================
  // 渲染：空状态
  // ============================================
  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-10 px-4">
      <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
        <Activity size={20} className="text-emerald-400" />
      </div>
      <p className="text-[12px] text-gray-300 font-medium mb-1">暂无流量数据 · 请先发布内容</p>
      <p className="text-[10px] text-gray-600 font-mono text-center max-w-[280px]">
        GF 输出层流量闭环依赖 DSO 发布的真实内容。请在 DSO 决策层发布内容后，
        录入各平台流量回采数据，系统将自动生成归因分析与回流反馈。
      </p>
      {dsoDecisions.length > 0 && (
        <div className="mt-4 w-full max-w-[360px] rounded-md border border-emerald-500/20 bg-emerald-500/[0.03] p-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Database size={10} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-emerald-300">
              检测到 DSO 已发布 {dsoDecisions.length} 条内容，可录入流量数据
            </span>
          </div>
          <div className="space-y-1 max-h-[120px] overflow-y-auto">
            {dsoDecisions.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[9px] font-mono">
                <span className="text-[10px]">{PLATFORM_DISPLAY[String(d.platform || "")]?.icon || "📌"}</span>
                <span className="text-gray-300 truncate flex-1">{dsoTitle(d)}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowPerfForm(true)}
            className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors"
          >
            <Plus size={10} /> 录入流量数据
          </button>
        </div>
      )}
    </div>
  );

  // ============================================
  // 渲染：统计概览栏
  // ============================================
  const renderStatsBar = () => {
    const stats = [
      { label: "内容数", value: formatNumber(state.performances.length), icon: FileText, color: "#10B981" },
      { label: "总阅读", value: formatNumber(totalViews), icon: Eye, color: "#4ADE80" },
      { label: "总线索", value: formatNumber(totalLeads), icon: Users, color: "#22D3EE" },
      { label: "成交转化率", value: `${(overallConversionRate * 100).toFixed(1)}%`, icon: Target, color: "#34D399" },
      { label: "优质爆款", value: formatNumber(excellentCount), icon: Flame, color: "#F59E0B" },
    ];
    return (
      <div className="grid grid-cols-5 gap-1.5 mb-3">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2 flex flex-col items-center justify-center"
            >
              <Icon size={11} style={{ color: s.color }} />
              <div className="text-[12px] font-mono font-bold text-white mt-1">{s.value}</div>
              <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">{s.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================
  // 渲染：子模块1 流量回采
  // ============================================
  const renderTrafficTab = () => (
    <div className="space-y-3">
      {/* 流量趋势图（recharts LineChart） */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={11} className="text-emerald-400" />
          <span className="text-[11px] font-medium text-white">流量趋势（按日 · 各平台阅读量）</span>
        </div>
        {trafficTrendData.length === 0 ? (
          <p className="text-[10px] text-gray-600 text-center py-4 font-mono">暂无流量趋势数据</p>
        ) : (
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trafficTrendData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#6B7280", fontFamily: "monospace" }} />
                <YAxis tick={{ fontSize: 9, fill: "#6B7280", fontFamily: "monospace" }} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(10,14,26,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "6px",
                    fontSize: "10px",
                    fontFamily: "monospace",
                  }}
                  labelStyle={{ color: "#E5E7EB" }}
                />
                <Legend wrapperStyle={{ fontSize: "9px", fontFamily: "monospace" }} iconType="circle" />
                {Object.keys(PLATFORM_DISPLAY).map((pf) => (
                  <Line
                    key={pf}
                    type="monotone"
                    dataKey={pf}
                    name={PLATFORM_DISPLAY[pf].name}
                    stroke={PLATFORM_DISPLAY[pf].color}
                    strokeWidth={1.5}
                    dot={{ r: 2 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* 关键词排名监测 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="flex items-center gap-1.5 mb-2">
          <Search size={11} className="text-emerald-400" />
          <span className="text-[11px] font-medium text-white">关键词排名监测</span>
          <span className="text-[9px] font-mono text-gray-500">（取最优排名）</span>
        </div>
        {keywordRankingRows.length === 0 ? (
          <p className="text-[10px] text-gray-600 text-center py-3 font-mono">暂无关键词排名数据</p>
        ) : (
          <div className="space-y-1 max-h-[180px] overflow-y-auto">
            {keywordRankingRows.map((kr, i) => {
              const trendUp = kr.previousRank !== undefined && kr.rank < kr.previousRank;
              const trendDown = kr.previousRank !== undefined && kr.rank > kr.previousRank;
              return (
                <div
                  key={i}
                  className="flex items-center justify-between py-1 px-2 rounded bg-white/[0.01] border border-white/[0.04]"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] text-gray-200 truncate">{kr.keyword}</span>
                    <span
                      className="text-[8px] font-mono px-1 py-0.5 rounded"
                      style={{
                        background: `${PLATFORM_DISPLAY[kr.platform]?.color || "#6B7280"}20`,
                        color: PLATFORM_DISPLAY[kr.platform]?.color || "#6B7280",
                      }}
                    >
                      {PLATFORM_DISPLAY[kr.platform]?.name || kr.platform}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-mono text-gray-400 truncate max-w-[120px]">
                      {kr.contentTitle}
                    </span>
                    <span className="text-[12px] font-mono font-bold text-emerald-400">#{kr.rank}</span>
                    {trendUp && <ArrowUpRight size={10} className="text-emerald-400" />}
                    {trendDown && <ArrowDownRight size={10} className="text-red-400" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* AI 提及仪表盘 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="flex items-center gap-1.5 mb-2">
          <Brain size={11} className="text-emerald-400" />
          <span className="text-[11px] font-medium text-white">品牌词 AI 提及监测</span>
          <span className="text-[9px] font-mono text-gray-500">（4 大模型）</span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {aiMentionSummary.map((ai) => (
            <div
              key={ai.key}
              className="rounded border border-white/[0.04] bg-white/[0.01] p-1.5 flex flex-col items-center"
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white mb-1"
                style={{ background: ai.color }}
              >
                {ai.short}
              </div>
              <div className="text-[9px] font-mono text-gray-300">{ai.name}</div>
              <div className="text-[14px] font-mono font-bold mt-0.5" style={{ color: ai.color }}>
                {ai.total}
              </div>
              <div className="flex items-center gap-1 mt-1 text-[8px] font-mono">
                <span className="text-emerald-400">+{ai.positive}</span>
                <span className="text-gray-500">·{ai.neutral}</span>
                <span className="text-red-400">-{ai.negative}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 负面舆情告警 */}
      <div className="rounded-md border border-red-500/15 bg-red-500/[0.02] p-2">
        <div className="flex items-center gap-1.5 mb-2">
          <AlertTriangle size={11} className="text-red-400" />
          <span className="text-[11px] font-medium text-white">负面舆情监控</span>
          <span className="text-[9px] font-mono text-gray-500">
            （{allNegativeAlerts.length} 条告警）
          </span>
        </div>
        {allNegativeAlerts.length === 0 ? (
          <p className="text-[10px] text-emerald-400/70 text-center py-3 font-mono">
            ✓ 未监测到负面舆情
          </p>
        ) : (
          <div className="space-y-1 max-h-[160px] overflow-y-auto">
            {allNegativeAlerts.map((a, i) => (
              <div
                key={i}
                className="flex items-start gap-2 py-1 px-2 rounded bg-white/[0.01] border border-white/[0.04]"
              >
                <span
                  className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0"
                  style={{ background: `${SEVERITY_META[a.severity].color}20`, color: SEVERITY_META[a.severity].color }}
                >
                  {SEVERITY_META[a.severity].label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-[9px] font-mono text-gray-400">{a.source}</span>
                    <span className="text-[9px] font-mono text-gray-600">·</span>
                    <span className="text-[9px] font-mono text-gray-500 truncate">{a.contentTitle}</span>
                  </div>
                  <div className="text-[10px] text-gray-300">{a.content}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // 渲染：子模块2 线索归集
  // ============================================
  const renderLeadsTab = () => {
    const filteredLeads = state.leads.filter((l) => {
      if (leadSourceFilter !== "all" && l.source !== leadSourceFilter) return false;
      if (leadStatusFilter !== "all" && l.status !== leadStatusFilter) return false;
      return true;
    });
    const maxFunnel = Math.max(...leadFunnel.map((f) => f.count), 1);

    return (
      <div className="space-y-3">
        {/* 转化漏斗 */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <Filter size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">线索转化漏斗</span>
          </div>
          <div className="space-y-1.5">
            {leadFunnel.map((f, i) => {
              const widthPct = (f.count / maxFunnel) * 100;
              const rate = i > 0 && leadFunnel[i - 1].count > 0 ? (f.count / leadFunnel[i - 1].count) * 100 : 100;
              return (
                <div key={f.stage} className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-gray-400 w-12 flex-shrink-0">{f.stage}</span>
                  <div className="flex-1 h-5 rounded bg-white/[0.02] overflow-hidden relative">
                    <div
                      className="h-full rounded flex items-center justify-end px-2 transition-all"
                      style={{ width: `${Math.max(widthPct, 8)}%`, background: `${f.color}40`, borderRight: `2px solid ${f.color}` }}
                    >
                      <span className="text-[10px] font-mono font-bold text-white">{f.count}</span>
                    </div>
                  </div>
                  <span className="text-[9px] font-mono text-gray-500 w-10 text-right flex-shrink-0">
                    {i > 0 ? `${rate.toFixed(0)}%` : "100%"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 筛选器 */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-mono text-gray-500">来源：</span>
            <button
              onClick={() => setLeadSourceFilter("all")}
              className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                leadSourceFilter === "all" ? "bg-emerald-500/20 text-emerald-300" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              全部
            </button>
            {(Object.keys(LEAD_SOURCE_META) as LeadSource[]).map((s) => (
              <button
                key={s}
                onClick={() => setLeadSourceFilter(s)}
                className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  leadSourceFilter === s ? "bg-emerald-500/20 text-emerald-300" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {LEAD_SOURCE_META[s].label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-mono text-gray-500">状态：</span>
            <button
              onClick={() => setLeadStatusFilter("all")}
              className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                leadStatusFilter === "all" ? "bg-emerald-500/20 text-emerald-300" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              全部
            </button>
            {(Object.keys(LEAD_STATUS_META) as LeadStatus[]).map((s) => (
              <button
                key={s}
                onClick={() => setLeadStatusFilter(s)}
                className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  leadStatusFilter === s ? "bg-emerald-500/20 text-emerald-300" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {LEAD_STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>

        {/* 线索列表 */}
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {filteredLeads.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-4 font-mono">暂无符合条件的线索</p>
          ) : (
            filteredLeads.map((lead) => {
              const SourceIcon = LEAD_SOURCE_META[lead.source].icon;
              const sourceContent = state.performances.find((p) => p.contentId === lead.sourceContentId);
              return (
                <div
                  key={lead.id}
                  className="py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01]"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <SourceIcon size={10} style={{ color: LEAD_SOURCE_META[lead.source].color }} />
                      <span className="text-[11px] text-gray-200 font-medium truncate">
                        {lead.contactInfo}
                      </span>
                      {lead.value && (
                        <span className="text-[9px] font-mono text-emerald-400 flex-shrink-0">
                          {formatMoney(lead.value)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <select
                        value={lead.status}
                        onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value as LeadStatus)}
                        className="text-[9px] font-mono bg-white/[0.04] text-gray-300 rounded px-1 py-0.5 border border-white/[0.06]"
                        style={{ color: LEAD_STATUS_META[lead.status].color }}
                      >
                        {(Object.keys(LEAD_STATUS_META) as LeadStatus[]).map((s) => (
                          <option key={s} value={s} className="bg-gray-900 text-gray-200">
                            {LEAD_STATUS_META[s].label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleDeleteLead(lead.id)}
                        className="text-gray-600 hover:text-red-400"
                        title="删除线索"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                  {/* 来源标签 */}
                  <div className="flex items-center gap-1 flex-wrap text-[8px] font-mono">
                    <span
                      className="px-1 py-0.5 rounded"
                      style={{
                        background: `${LEAD_SOURCE_META[lead.source].color}20`,
                        color: LEAD_SOURCE_META[lead.source].color,
                      }}
                    >
                      {LEAD_SOURCE_META[lead.source].label}
                    </span>
                    {sourceContent && (
                      <span className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300 truncate max-w-[180px]">
                        来源：{sourceContent.contentTitle}
                      </span>
                    )}
                    {lead.sourceKeyword && (
                      <span className="px-1 py-0.5 rounded bg-blue-500/10 text-blue-300">
                        关键词：{lead.sourceKeyword}
                      </span>
                    )}
                    <span className="px-1 py-0.5 rounded bg-white/[0.04] text-gray-500">
                      {formatTime(lead.createdAt)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：子模块3 效果归因
  // ============================================
  const renderAttributionTab = () => {
    const sorted = [...attributions].sort((a, b) => b.roi - a.roi);

    return (
      <div className="space-y-3">
        {/* 质量标签分布饼图 */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <Award size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">质量标签分布</span>
          </div>
          {qualityDistribution.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-4 font-mono">暂无质量标签数据</p>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={qualityDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                  >
                    {qualityDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke="rgba(10,14,26,0.8)"
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "rgba(10,14,26,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      fontSize: "10px",
                      fontFamily: "monospace",
                    }}
                    labelStyle={{ color: "#E5E7EB" }}
                    itemStyle={{ color: "#9CA3AF" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "9px", fontFamily: "monospace" }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* CAC / 转化率分析（BarChart） */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <DollarSign size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">CAC 与成交转化率分析</span>
          </div>
          {sorted.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-4 font-mono">暂无归因数据</p>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sorted.map((a) => ({ name: a.contentTitle.slice(0, 8), cac: a.cac, rate: a.closeConversionRate * 100 }))} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#6B7280", fontFamily: "monospace" }} />
                  <YAxis tick={{ fontSize: 9, fill: "#6B7280", fontFamily: "monospace" }} />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(10,14,26,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      fontSize: "10px",
                      fontFamily: "monospace",
                    }}
                    labelStyle={{ color: "#E5E7EB" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "9px", fontFamily: "monospace" }} iconType="circle" />
                  <Bar dataKey="cac" name="CAC(元)" fill="#10B981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="rate" name="成交转化率(%)" fill="#22D3EE" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 内容效果排行（按 ROI 排序） */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">内容效果排行（按 ROI 降序）</span>
          </div>
          <div className="space-y-1 max-h-[260px] overflow-y-auto">
            {sorted.map((a, i) => {
              const QIcon = QUALITY_META[a.qualityLabel].icon;
              return (
                <div
                  key={a.contentId}
                  className="py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01]"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[10px] font-mono text-gray-500 w-4 flex-shrink-0">#{i + 1}</span>
                      <span className="text-[11px] text-gray-200 font-medium truncate">{a.contentTitle}</span>
                    </div>
                    <span
                      className="text-[8px] font-mono px-1 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0"
                      style={{
                        background: `${QUALITY_META[a.qualityLabel].color}20`,
                        color: QUALITY_META[a.qualityLabel].color,
                      }}
                    >
                      <QIcon size={9} />
                      {QUALITY_META[a.qualityLabel].label}
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-1 text-[9px] font-mono">
                    <div className="text-center px-1 py-0.5 rounded bg-white/[0.02]">
                      <div className="text-gray-600">CAC</div>
                      <div className="text-emerald-400">{formatMoney(a.cac)}</div>
                    </div>
                    <div className="text-center px-1 py-0.5 rounded bg-white/[0.02]">
                      <div className="text-gray-600">内容成本</div>
                      <div className="text-gray-300">{formatMoney(a.contentCost)}</div>
                    </div>
                    <div className="text-center px-1 py-0.5 rounded bg-white/[0.02]">
                      <div className="text-gray-600">线索</div>
                      <div className="text-cyan-400">{a.leads}</div>
                    </div>
                    <div className="text-center px-1 py-0.5 rounded bg-white/[0.02]">
                      <div className="text-gray-600">成交</div>
                      <div className="text-emerald-400">{a.convertedLeads}</div>
                    </div>
                    <div className="text-center px-1 py-0.5 rounded bg-white/[0.02]">
                      <div className="text-gray-600">ROI</div>
                      <div className={a.roi >= 1 ? "text-emerald-400 font-bold" : "text-red-400"}>
                        {a.roi.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[9px] font-mono">
                    <span className="text-gray-500">线索转化率：{(a.leadConversionRate * 100).toFixed(1)}%</span>
                    <span className="text-gray-500">成交转化率：{(a.closeConversionRate * 100).toFixed(2)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：子模块4 数据回流
  // ============================================
  const renderFeedbackTab = () => {
    const lowFeedbacks = state.feedbacks.filter((f) => f.type === "low_performance");
    const highFeedbacks = state.feedbacks.filter((f) => f.type === "high_performance");

    return (
      <div className="space-y-3">
        {/* 顶部操作条 */}
        <div className="flex items-center justify-between rounded-md border border-emerald-500/15 bg-emerald-500/[0.02] p-2">
          <div className="flex items-center gap-1.5">
            <RotateCcw size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-emerald-300">
              已生成 {state.feedbacks.length} 条反馈（低效 {lowFeedbacks.length} · 优质 {highFeedbacks.length}）
            </span>
          </div>
          <button
            onClick={handleGenerateFeedbacks}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors"
          >
            <Sparkles size={10} />
            重新生成反馈
          </button>
        </div>

        {/* 低效内容反馈 */}
        <div className="rounded-md border border-red-500/15 bg-red-500/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle size={11} className="text-red-400" />
            <span className="text-[11px] font-medium text-white">低效内容反馈（→ GEO / DSO）</span>
          </div>
          {lowFeedbacks.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-3 font-mono">暂无低效内容反馈</p>
          ) : (
            <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
              {lowFeedbacks.map((f) => (
                <div
                  key={f.contentId}
                  className="py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01]"
                >
                  <div className="text-[11px] text-gray-200 font-medium mb-1">{f.contentTitle}</div>
                  <div className="text-[10px] text-gray-400 mb-1.5">{f.analysis}</div>
                  <div className="grid grid-cols-2 gap-1.5 text-[9px] font-mono">
                    {f.geoFeedback?.keywordOptimization && (
                      <div className="rounded bg-blue-500/[0.06] border border-blue-500/15 p-1">
                        <div className="text-blue-400 mb-0.5">→ GEO 关键词优化</div>
                        <div className="text-gray-400">{f.geoFeedback.keywordOptimization}</div>
                      </div>
                    )}
                    {f.dsoFeedback?.styleAdjustment && (
                      <div className="rounded bg-amber-500/[0.06] border border-amber-500/15 p-1">
                        <div className="text-amber-400 mb-0.5">→ DSO 文案风格</div>
                        <div className="text-gray-400">{f.dsoFeedback.styleAdjustment}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 优质内容复用模板 */}
        <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.03] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <Flame size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">优质爆款复用模板</span>
          </div>
          {highFeedbacks.length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-3 font-mono">暂无优质内容复用模板</p>
          ) : (
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
              {highFeedbacks.map((f) => (
                <div
                  key={f.contentId}
                  className="py-1.5 px-2 rounded border border-emerald-500/15 bg-white/[0.01]"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[11px] text-gray-200 font-medium truncate">{f.contentTitle}</span>
                    <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 flex-shrink-0">
                      爆款
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400 mb-1.5">{f.analysis}</div>
                  {f.reuseTemplate && (
                    <div className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5 space-y-1">
                      <div className="text-[9px] font-mono">
                        <span className="text-emerald-400">标题模板：</span>
                        <span className="text-gray-300">{f.reuseTemplate.titlePattern}</span>
                      </div>
                      <div className="text-[9px] font-mono">
                        <span className="text-emerald-400">结构：</span>
                        <span className="text-gray-300">{f.reuseTemplate.structure}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap text-[9px] font-mono">
                        <span className="text-emerald-400">卖点：</span>
                        {f.reuseTemplate.sellingPoints.map((sp, i) => (
                          <span key={i} className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300">
                            {sp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Obsidian 写入记录 */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <Database size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-white">Obsidian 知识库写入记录</span>
          </div>
          {highFeedbacks.filter((f) => f.obsidianWriteback).length === 0 ? (
            <p className="text-[10px] text-gray-600 text-center py-3 font-mono">暂无 Obsidian 写入记录</p>
          ) : (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {highFeedbacks
                .filter((f) => f.obsidianWriteback)
                .map((f) => (
                  <div
                    key={f.contentId}
                    className="flex items-start gap-2 py-1 px-2 rounded border border-white/[0.04] bg-white/[0.01]"
                  >
                    <FileText size={10} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="text-[9px] font-mono text-gray-300 truncate">
                          {f.obsidianWriteback!.path}
                        </span>
                        {f.obsidianWriteback!.written ? (
                          <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 flex items-center gap-0.5 flex-shrink-0">
                            <CheckCircle2 size={9} /> 已写入
                          </span>
                        ) : (
                          <button
                            onClick={() => handleWriteToObsidian(f.contentId)}
                            className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-600/60 hover:bg-emerald-600 text-white flex items-center gap-0.5 flex-shrink-0"
                          >
                            <Send size={9} /> 写入
                          </button>
                        )}
                      </div>
                      <div className="text-[9px] font-mono text-gray-500 truncate">
                        {f.obsidianWriteback!.content.slice(0, 80)}...
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：内容绩效列表（用于流量回采 Tab 下方）
  // ============================================
  // （已内联至各 Tab，此处不再单独渲染）

  // ============================================
  // 主渲染
  // ============================================
  const tabs = [
    { id: "traffic" as const, label: "流量回采", icon: Activity, color: "#10B981" },
    { id: "leads" as const, label: "线索归集", icon: Users, color: "#22D3EE" },
    { id: "attribution" as const, label: "效果归因", icon: Target, color: "#4ADE80" },
    { id: "feedback" as const, label: "数据回流", icon: RotateCcw, color: "#34D399" },
  ];

  return (
    <div className="rounded-lg border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.03] to-green-500/[0.02] p-3">
      {/* ============ 头部 ============ */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Layers size={14} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">GF 输出层流量闭环</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              流量回采 · 线索归集 · 效果归因 · 数据回流
              <span className="text-emerald-400 ml-1">· GEO+DSO 商业化改造 第 4 层</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowPerfForm(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 rounded transition-colors"
            title="录入内容绩效（流量数据）"
          >
            <Plus size={10} /> 录入流量
          </button>
          <button
            onClick={() => setShowLeadForm(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors"
            title="归集新线索"
          >
            <Plus size={10} /> 录入线索
          </button>
          <button
            onClick={refresh}
            className="p-1 text-gray-400 hover:text-gray-200"
            title="刷新数据"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ============ toast 提示 ============ */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md border overflow-hidden ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/20"
                : "bg-red-500/10 border-red-500/20"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
            ) : (
              <XCircle size={11} className="text-red-400 flex-shrink-0" />
            )}
            <span
              className={`text-[10px] ${toast.type === "success" ? "text-emerald-300" : "text-red-300"}`}
            >
              {toast.message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ 加载中 ============ */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={18} className="animate-spin text-emerald-400" />
          <span className="ml-2 text-[11px] text-gray-400 font-mono">正在读取流量闭环数据...</span>
        </div>
      )}

      {/* ============ 空状态 ============ */}
      {!loading && !hasAnyData && renderEmpty()}

      {/* ============ 主面板 ============ */}
      {!loading && hasAnyData && (
        <>
          {/* 统计概览 */}
          {renderStatsBar()}

          {/* Tab 切换 */}
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono transition-all ${
                    active ? "text-white bg-white/[0.04]" : "text-gray-500 hover:text-gray-300"
                  }`}
                  style={active ? { color: tab.color } : {}}
                >
                  <Icon size={10} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab 内容区 */}
          <div className="max-h-[480px] overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
              >
                {activeTab === "traffic" && renderTrafficTab()}
                {activeTab === "leads" && renderLeadsTab()}
                {activeTab === "attribution" && renderAttributionTab()}
                {activeTab === "feedback" && renderFeedbackTab()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* 内容绩效快速管理（删除入口） */}
          {activeTab === "traffic" && state.performances.length > 0 && (
            <div className="mt-3 pt-2 border-t border-white/[0.04]">
              <div className="text-[9px] font-mono text-gray-500 mb-1.5 uppercase tracking-wider">
                已录入内容绩效（{state.performances.length}）· 点击删除
              </div>
              <div className="flex flex-wrap gap-1">
                {state.performances.map((p) => (
                  <div
                    key={p.contentId}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono"
                  >
                    <span className="text-[10px]">{PLATFORM_DISPLAY[p.platform]?.icon || "📌"}</span>
                    <span className="text-gray-300 truncate max-w-[100px]">{p.contentTitle}</span>
                    <button
                      onClick={() => handleDeletePerformance(p.contentId)}
                      className="text-gray-600 hover:text-red-400"
                      title="删除"
                    >
                      <X size={9} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ 录入表单：内容绩效 ============ */}
      <AnimatePresence>
        {showPerfForm && (
          <PerformanceForm
            dsoDecisions={dsoDecisions}
            onSubmit={handleAddPerformance}
            onCancel={() => setShowPerfForm(false)}
          />
        )}
      </AnimatePresence>

      {/* ============ 录入表单：线索 ============ */}
      <AnimatePresence>
        {showLeadForm && (
          <LeadForm
            performances={state.performances}
            onSubmit={handleAddLead}
            onCancel={() => setShowLeadForm(false)}
          />
        )}
      </AnimatePresence>

      {/* ============ 底部说明 ============ */}
      <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center justify-between text-[9px] font-mono text-gray-600">
        <span className="flex items-center gap-1">
          <Database size={9} className="text-emerald-600" />
          数据来源：localStorage(xeaos_gf_output) 人工录入 · DSO 联动(xeaos_dso_decision)
        </span>
        <span>
          {state.performances.length} 绩效 · {state.leads.length} 线索 · {state.feedbacks.length} 反馈
          {dsoDecisions.length > 0 && ` · DSO ${dsoDecisions.length} 条`}
        </span>
      </div>
    </div>
  );
}

// ============================================
// 子组件：内容绩效录入表单
// ============================================
interface PerformanceFormProps {
  dsoDecisions: DsoDecision[];
  onSubmit: (perf: Omit<ContentPerformance, "contentId" | "qualityLabel">) => void;
  onCancel: () => void;
}

function PerformanceForm({ dsoDecisions, onSubmit, onCancel }: PerformanceFormProps) {
  const [contentTitle, setContentTitle] = useState("");
  const [platform, setPlatform] = useState<string>("wechat");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [views, setViews] = useState("");
  const [likes, setLikes] = useState("");
  const [comments, setComments] = useState("");
  const [shares, setShares] = useState("");
  const [leads, setLeads] = useState("");
  const [contentCost, setContentCost] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<{ keyword: string; rank: number; previousRank?: number }[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // AI 提及（4 平台）
  const [aiCounts, setAiCounts] = useState<Record<string, string>>({});
  const [aiSentiments, setAiSentiments] = useState<Record<string, "positive" | "neutral" | "negative">>({});

  const handlePickDso = (d: DsoDecision) => {
    setContentTitle(dsoTitle(d));
    if (d.platform && PLATFORM_DISPLAY[String(d.platform)]) setPlatform(String(d.platform));
  };

  const handleAddKeyword = () => {
    const parts = keywordInput.trim().split(/[\s,，]+/);
    if (parts.length >= 2) {
      const rank = parseInt(parts[1], 10);
      if (Number.isFinite(rank)) {
        setKeywords((prev) => [...prev, { keyword: parts[0], rank }]);
        setKeywordInput("");
      }
    }
  };

  const handleSubmit = () => {
    if (!contentTitle.trim()) {
      alert("请输入内容标题");
      return;
    }
    if (!views || parseInt(views, 10) <= 0) {
      alert("请输入有效阅读量");
      return;
    }
    const aiMentions = AI_PLATFORMS.map((p) => {
      const cnt = parseInt(aiCounts[p.key] || "0", 10);
      if (!Number.isFinite(cnt) || cnt <= 0) return null;
      return {
        platform: p.key,
        count: cnt,
        sentiment: aiSentiments[p.key] || "neutral",
      };
    }).filter(Boolean) as ContentPerformance["aiMentions"];

    const perf: Omit<ContentPerformance, "contentId" | "qualityLabel"> = {
      contentTitle: contentTitle.trim(),
      platform,
      date,
      views: parseInt(views, 10) || 0,
      likes: parseInt(likes, 10) || 0,
      comments: parseInt(comments, 10) || 0,
      shares: parseInt(shares, 10) || 0,
      leads: parseInt(leads, 10) || 0,
      cac: 0, // 由归因派生计算
      contentCost: parseFloat(contentCost) || 0,
      keywordRankings: keywords,
      aiMentions,
    };
    onSubmit(perf);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] max-h-[85vh] overflow-y-auto rounded-lg border border-emerald-500/20 bg-[#0a0e1a] p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Database size={14} className="text-emerald-400" />
            <h3 className="text-[13px] font-semibold text-white">录入内容绩效（流量回采）</h3>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-300">
            <X size={14} />
          </button>
        </div>

        {/* DSO 联动：选择已发布内容 */}
        {dsoDecisions.length > 0 && (
          <div className="mb-3 rounded-md border border-emerald-500/15 bg-emerald-500/[0.03] p-2">
            <div className="text-[9px] font-mono text-emerald-300 mb-1.5">
              从 DSO 已发布内容中选择（联动 xeaos_dso_decision）
            </div>
            <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto">
              {dsoDecisions.slice(0, 10).map((d, i) => (
                <button
                  key={i}
                  onClick={() => handlePickDso(d)}
                  className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-gray-300 hover:bg-emerald-500/20 hover:text-emerald-300 border border-white/[0.06]"
                >
                  {PLATFORM_DISPLAY[String(d.platform || "")]?.icon || "📌"} {dsoTitle(d).slice(0, 12)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 基本信息 */}
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-mono text-gray-400 block mb-1">内容标题 *</label>
            <input
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              placeholder="请输入内容标题"
              className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">平台 *</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 focus:outline-none focus:border-emerald-500/40"
              >
                {Object.keys(PLATFORM_DISPLAY).map((p) => (
                  <option key={p} value={p} className="bg-gray-900">
                    {PLATFORM_DISPLAY[p].name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">录入日期 *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>

          {/* 流量数据 */}
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-1">阅读量 *</label>
              <input
                type="number"
                value={views}
                onChange={(e) => setViews(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-1">点赞</label>
              <input
                type="number"
                value={likes}
                onChange={(e) => setLikes(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-1">评论</label>
              <input
                type="number"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-1">转发</label>
              <input
                type="number"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">线索数</label>
              <input
                type="number"
                value={leads}
                onChange={(e) => setLeads(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">内容成本（元）</label>
              <input
                type="number"
                value={contentCost}
                onChange={(e) => setContentCost(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>

          {/* 高级选项：关键词排名 + AI 提及 */}
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 hover:text-emerald-300"
          >
            {showAdvanced ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            高级选项（关键词排名 + AI 提及）
          </button>
          <AnimatePresence>
            {showAdvanced && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden space-y-2"
              >
                {/* 关键词排名 */}
                <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                  <div className="text-[10px] font-mono text-gray-400 mb-1">关键词排名（格式：关键词 排名）</div>
                  <div className="flex items-center gap-1 mb-1">
                    <input
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddKeyword())}
                      placeholder="如：XEAOS 5"
                      className="flex-1 text-[11px] px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                    />
                    <button
                      onClick={handleAddKeyword}
                      className="px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 rounded"
                    >
                      添加
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {keywords.map((k, i) => (
                      <span
                        key={i}
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 flex items-center gap-1"
                      >
                        {k.keyword} #{k.rank}
                        <button onClick={() => setKeywords((prev) => prev.filter((_, idx) => idx !== i))}>
                          <X size={9} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* AI 提及 */}
                <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                  <div className="text-[10px] font-mono text-gray-400 mb-1">品牌词 AI 提及量（4 大模型）</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {AI_PLATFORMS.map((p) => (
                      <div key={p.key} className="flex items-center gap-1">
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                          style={{ background: p.color }}
                        >
                          {p.short}
                        </span>
                        <input
                          type="number"
                          value={aiCounts[p.key] || ""}
                          onChange={(e) => setAiCounts((prev) => ({ ...prev, [p.key]: e.target.value }))}
                          placeholder="0"
                          className="w-14 text-[10px] px-1.5 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                        />
                        <select
                          value={aiSentiments[p.key] || "neutral"}
                          onChange={(e) =>
                            setAiSentiments((prev) => ({
                              ...prev,
                              [p.key]: e.target.value as "positive" | "neutral" | "negative",
                            }))
                          }
                          className="text-[9px] px-1 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-gray-300"
                        >
                          <option value="positive" className="bg-gray-900">正</option>
                          <option value="neutral" className="bg-gray-900">中</option>
                          <option value="negative" className="bg-gray-900">负</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-white/[0.06]">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-gray-200 rounded"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-mono text-white bg-emerald-600 hover:bg-emerald-500 rounded transition-colors"
          >
            <CheckCircle2 size={11} /> 保存绩效
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：线索录入表单
// ============================================
interface LeadFormProps {
  performances: ContentPerformance[];
  onSubmit: (lead: Omit<Lead, "id" | "createdAt">) => void;
  onCancel: () => void;
}

function LeadForm({ performances, onSubmit, onCancel }: LeadFormProps) {
  const [source, setSource] = useState<LeadSource>("wechat");
  const [sourceContentId, setSourceContentId] = useState("");
  const [sourceKeyword, setSourceKeyword] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<LeadStatus>("new");

  const handleSubmit = () => {
    if (!contactInfo.trim()) {
      alert("请输入联系方式");
      return;
    }
    onSubmit({
      source,
      sourceContentId: sourceContentId || undefined,
      sourceKeyword: sourceKeyword.trim() || undefined,
      contactInfo: contactInfo.trim(),
      value: parseFloat(value) || undefined,
      status,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] rounded-lg border border-emerald-500/20 bg-[#0a0e1a] p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-emerald-400" />
            <h3 className="text-[13px] font-semibold text-white">归集新线索</h3>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-300">
            <X size={14} />
          </button>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">来源触点 *</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as LeadSource)}
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 focus:outline-none focus:border-emerald-500/40"
              >
                {(Object.keys(LEAD_SOURCE_META) as LeadSource[]).map((s) => (
                  <option key={s} value={s} className="bg-gray-900">
                    {LEAD_SOURCE_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">线索状态</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as LeadStatus)}
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 focus:outline-none focus:border-emerald-500/40"
              >
                {(Object.keys(LEAD_STATUS_META) as LeadStatus[]).map((s) => (
                  <option key={s} value={s} className="bg-gray-900">
                    {LEAD_STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-mono text-gray-400 block mb-1">联系方式 *</label>
            <input
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder="微信/手机号/邮箱"
              className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono text-gray-400 block mb-1">来源内容（打标签）</label>
            <select
              value={sourceContentId}
              onChange={(e) => setSourceContentId(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 focus:outline-none focus:border-emerald-500/40"
            >
              <option value="" className="bg-gray-900">— 不关联具体内容 —</option>
              {performances.map((p) => (
                <option key={p.contentId} value={p.contentId} className="bg-gray-900">
                  {PLATFORM_DISPLAY[p.platform]?.name || p.platform} · {p.contentTitle}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">来源关键词</label>
              <input
                value={sourceKeyword}
                onChange={(e) => setSourceKeyword(e.target.value)}
                placeholder="如：XEAOS"
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-400 block mb-1">预估价值（元）</label>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0"
                className="w-full text-[11px] px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-white/[0.06]">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-gray-200 rounded"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-mono text-white bg-emerald-600 hover:bg-emerald-500 rounded transition-colors"
          >
            <CheckCircle2 size={11} /> 归集线索
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
